import { useMemo, useState } from "react";
import { Button, MenuItem, TextField } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

import { Sidebar } from "../../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { CustomTable } from "../../../components/CustomTable/CustomTable";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";
import {
  TFleetDocumentsFilters,
  useGetFleetDocuments,
  useGetFleetDocumentsSummary,
} from "../../../api/document";
import { useGetAllVehiculesFleet } from "../../../api/vehicule";
import { useGetAllEmployeeWithoutVehicle } from "../../../api/employee";
import { getCurrentFleetId } from "../../../utils/getCurrentFleet";
import { useServerPagination } from "../../../hooks/useServerPagination";
import { FleetReportModal } from "../FleetReportModal/FleetReportModal";
import {
  buildColumnsDocumentsList,
  getDocumentTypeLabel,
} from "./columnsDocumentsList";
import useStyles from "./styles";

/**
 * GED — page « Documents ».
 *
 * Vue transverse de tous les documents de la flotte (contrats, factures,
 * assurances, contrôles techniques, ...) stockés en BDD (table strongbox),
 * filtrable par type, source, véhicule, collaborateur et période.
 */
export const ListDocuments: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const fleetId = getCurrentFleetId();

  const pagination = useServerPagination();
  const [filters, setFilters] = useState<TFleetDocumentsFilters>({
    sort: "date_desc",
  });
  // Report modal — kept local to the page since it's only reachable from here.
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const { documents, totalCount, isPending } = useGetFleetDocuments(
    fleetId,
    pagination.page,
    pagination.perPage,
    { ...filters, search: pagination.search }
  );
  const { summary } = useGetFleetDocumentsSummary(fleetId);
  const { vehicules } = useGetAllVehiculesFleet(fleetId, 1, 100);
  const { employees } = useGetAllEmployeeWithoutVehicle(fleetId);

  /** Any filter change restarts from page 1 (backend pagination). */
  const applyFilter = (partial: Partial<TFleetDocumentsFilters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    pagination.resetToFirstPage();
  };

  const resetFilters = () => {
    setFilters({ sort: "date_desc" });
    pagination.resetToFirstPage();
  };

  const openDocument = (doc: TFleetDocument) => {
    if (doc.scan_id) window.open(doc.scan_id, "_blank", "noopener,noreferrer");
  };

  const columns = useMemo(
    () => buildColumnsDocumentsList(t, { onOpenDocument: openDocument }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  /** Type options come from the data actually present in the fleet. */
  const typeOptions = useMemo(
    () =>
      (summary?.by_type ?? [])
        .filter((entry) => !!entry.type)
        .map((entry) => ({
          value: entry.type as string,
          label: `${getDocumentTypeLabel(t, entry.type)} (${entry.count})`,
        })),
    [summary, t]
  );

  const vehicleOptions = useMemo(
    () =>
      (vehicules ?? [])
        .map((v: any) => ({
          value: v?.vehicle_id ?? v?.vehicle?.id ?? v?.vehicleId,
          label:
            v?.vehicle?.number ?? v?.immatriculation ?? v?.number ?? "",
        }))
        .filter((option) => !!option.value && !!option.label),
    [vehicules]
  );

  const collaboratorOptions = useMemo(
    () =>
      (employees ?? [])
        .map((employee) => ({
          value: employee.user_id,
          label: [employee.firstname, employee.lastname]
            .filter(Boolean)
            .join(" "),
        }))
        .filter((option) => !!option.value && !!option.label),
    [employees]
  );

  const sourceOptions: { value: TFleetDocumentSource; label: string }[] = [
    { value: "vehicle", label: t("ged.sourceVehicle", "Véhicule") },
    { value: "collaborator", label: t("ged.sourceCollaborator", "Collaborateur") },
    { value: "sinister", label: t("ged.sourceSinister", "Sinistre") },
    { value: "event", label: t("ged.sourceEvent", "Événement") },
    { value: "contract", label: t("ged.sourceContract", "Contrat") },
  ];

  const hasActiveFilters =
    !!filters.search ||
    !!filters.type ||
    !!filters.vehicle_id ||
    !!filters.user_id ||
    !!filters.source ||
    !!filters.start_date ||
    !!filters.end_date;

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      <Sidebar
        styles={{
          sidebarRightRoot: classes.documentsRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.containerHeader}>
          <CustomTypography
            variant="h2"
            text={t("ged.title", "Documents")}
            isUppercase
          />
          {/*
           * Fleet-wide report generation entry point.
           * Placed on the Documents page as requested by product: this is the
           * natural home for "exports & reports" in the current IA. If a
           * dedicated /reports route ever appears, the button can move but
           * the modal itself stays reusable.
           *
           * We use MUI's Button here (rather than CustomButton) because we
           * want a leading icon: CustomButton does not expose startIcon.
           */}
          <Button
            variant={"contained"}
            size={"small"}
            startIcon={<DownloadIcon />}
            onClick={() => setReportModalOpen(true)}
          >
            {t("fleetReports.openModal", "Generate a report")}
          </Button>
        </div>

        {/* ── Compteurs ─────────────────────────────────────────── */}
        {summary && (
          <div className={classes.summaryRow}>
            <div className={classes.summaryChip}>
              <b>{summary.total}</b>
              {t("ged.totalDocuments", "documents")}
            </div>
            <div className={classes.summaryChip}>
              <b>{summary.last_30_days}</b>
              {t("ged.last30Days", "ajoutés sur 30 jours")}
            </div>
            {summary.by_type.slice(0, 4).map(
              (entry) =>
                entry.type && (
                  <div key={entry.type} className={classes.summaryChip}>
                    <b>{entry.count}</b>
                    {getDocumentTypeLabel(t, entry.type)}
                  </div>
                )
            )}
          </div>
        )}

        {/* ── Filtres ───────────────────────────────────────────── */}
        <div className={classes.filtersBar}>
          <TextField
            select
            size="small"
            className={classes.filterControl}
            label={t("ged.filterType", "Type")}
            value={filters.type ?? ""}
            onChange={(e) => applyFilter({ type: e.target.value })}
          >
            <MenuItem value="">{t("ged.allTypes", "Tous les types")}</MenuItem>
            {typeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            className={classes.filterControl}
            label={t("ged.filterSource", "Source")}
            value={filters.source ?? ""}
            onChange={(e) =>
              applyFilter({ source: e.target.value as TFleetDocumentSource })
            }
          >
            <MenuItem value="">
              {t("ged.allSources", "Toutes les sources")}
            </MenuItem>
            {sourceOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            className={classes.filterControl}
            label={t("ged.filterVehicle", "Véhicule")}
            value={filters.vehicle_id ?? ""}
            onChange={(e) =>
              applyFilter({
                vehicle_id: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          >
            <MenuItem value="">
              {t("ged.allVehicles", "Tous les véhicules")}
            </MenuItem>
            {vehicleOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            className={classes.filterControl}
            label={t("ged.filterCollaborator", "Collaborateur")}
            value={filters.user_id ?? ""}
            onChange={(e) =>
              applyFilter({
                user_id: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          >
            <MenuItem value="">
              {t("ged.allCollaborators", "Tous les collaborateurs")}
            </MenuItem>
            {collaboratorOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="date"
            className={classes.dateControl}
            label={t("ged.filterStartDate", "Du")}
            InputLabelProps={{ shrink: true }}
            value={filters.start_date ?? ""}
            onChange={(e) => applyFilter({ start_date: e.target.value })}
          />

          <TextField
            size="small"
            type="date"
            className={classes.dateControl}
            label={t("ged.filterEndDate", "Au")}
            InputLabelProps={{ shrink: true }}
            value={filters.end_date ?? ""}
            onChange={(e) => applyFilter({ end_date: e.target.value })}
          />

          <TextField
            select
            size="small"
            className={classes.filterControl}
            label={t("ged.filterSort", "Tri")}
            value={filters.sort ?? "date_desc"}
            onChange={(e) =>
              applyFilter({
                sort: e.target.value as TFleetDocumentsFilters["sort"],
              })
            }
          >
            <MenuItem value="date_desc">
              {t("ged.sortDateDesc", "Plus récents")}
            </MenuItem>
            <MenuItem value="date_asc">
              {t("ged.sortDateAsc", "Plus anciens")}
            </MenuItem>
            <MenuItem value="name_asc">{t("ged.sortNameAsc", "Nom A→Z")}</MenuItem>
            <MenuItem value="name_desc">
              {t("ged.sortNameDesc", "Nom Z→A")}
            </MenuItem>
          </TextField>

          {hasActiveFilters && (
            <Button size="small" onClick={resetFilters}>
              {t("ged.resetFilters", "Réinitialiser")}
            </Button>
          )}
        </div>

        {/* ── Tableau ───────────────────────────────────────────── */}
        {/* Wrapped in an explicitly-classed element rather than relying on a
            `:last-child` selector to find it — see styles.tableCard. */}
        <div className={classes.tableCard}>
          <CustomTable
            columns={columns}
            data={documents}
            isSearchable
            isFilterFromBackend
            tablePagnationCount={totalCount}
            {...pagination.tableProps}
          />
        </div>
      </Sidebar>

      {/*
       * Rendered outside the Sidebar so its Dialog portal isn't affected by
       * the sidebar's overflow/height rules. Mounted only when open to keep
       * the section catalog fetch lazy.
       */}
      {reportModalOpen && (
        <FleetReportModal
          open={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
          fleetId={fleetId}
        />
      )}
    </>
  );
};
